import Embed from "@editorjs/embed";
import Header from "@editorjs/header";
import Image from "@editorjs/image";
import List from "@editorjs/list";
import Marker from "@editorjs/marker";
import Paragraph from "@editorjs/paragraph";
import Quote from "@editorjs/quote";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";
import { createReactEditorJS } from "react-editor-js";

import PropTypes from "prop-types";

class ReadOnly {
  static get toolbox() {
    return {};
  }

  constructor({ data }) {
    this.data = data;
  }

  render() {
    const wrapper = document.createElement("div");
    wrapper.classList.add("read-only");
    wrapper.innerHTML = this.data.text || "";
    wrapper.contentEditable = false;
    return wrapper;
  }

  save(blockContent) {
    return {
      text: this.data.text,
    };
  }
}

const EDITOR_JS_TOOLS = {
  embed: Embed,
  list: List,
  header: Header,
  quote: Quote,
  marker: Marker,
  paragraph: {
    class: Paragraph,
    inlineToolbar: ["bold", "italic"],
  },
  readonly: {
    class: ReadOnly,
    inlineToolbar: false,
    tunes: [],
  },
};

const ReactEditorJS = createReactEditorJS();

const Component = forwardRef((props, ref) => {
  const { imageUploader, ...editorProps } = props;
  const inputRef = useRef(null);

  const tools = useMemo(() => {
    if (!imageUploader) {
      return EDITOR_JS_TOOLS;
    }
    return {
      ...EDITOR_JS_TOOLS,
      image: {
        class: Image,
        config: {
          features: {
            caption: false,
          },
          uploader: {
            uploadByFile: async (file) => {
              const url = await imageUploader(file);
              return {
                success: 1,
                file: { url },
              };
            },
          },
        },
      },
    };
  }, [imageUploader]);

  useImperativeHandle(ref, () => {
    return {
      handleSave: () => {
        return inputRef.current.save();
      },
    };
  }, []);

  useEffect(() => {
    const hideReadonlyToolbars = () => {
      const blocks = document.querySelectorAll(".ce-block");
      blocks.forEach((block) => {
        if (block.querySelector(".read-only")) block.classList.add("read-only");
      });
    };
    const observer = new MutationObserver(hideReadonlyToolbars);
    observer.observe(document.body, { childList: true, subtree: true });
    hideReadonlyToolbars();
    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <div className="text-editor--wrapper">
      <ReactEditorJS
        onInitialize={(instance) => {
          inputRef.current = instance;
        }}
        tools={tools}
        {...editorProps}
        ref={inputRef}
      />
    </div>
  );
});

Component.prototype = {
  imageUploader: PropTypes.func,
  defaultValue: PropTypes.shape({
    blocks: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        type: PropTypes.string.isRequired,
        data: PropTypes.objectOf(PropTypes.number),
      }),
    ),
  }),
};

Component.defaultProps = {
  blocks: [],
};

export default Component;
